import React, { useEffect, useState } from "react";
import {
  Users, TrendingUp, Megaphone, Clapperboard, Download, GraduationCap,
  Heart, MessageCircleQuestion, Mail, Send, Briefcase, FolderKanban,
  AlertTriangle, CheckSquare, Clock, Flag, Calendar, Lightbulb, LayoutGrid
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PRI_COLORS = { high: "#C0392B", medium: "#E2A84B", low: "#8A7E6B" };

function isOverdue(d) {
  if (!d) return false;
  return new Date(d) < new Date() && new Date(d).toDateString() !== new Date().toDateString();
}

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function StatCard({ icon: Icon, label, value, accent, alert }) {
  return (
    <div className="rp-foam bg-white/55 p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: alert ? "#C0392B18" : accent ? "#262016" : "rgba(181,121,63,0.12)" }}>
        <Icon size={17} className={alert ? "text-red-500" : accent ? "text-[#EDC9A3]" : "text-[#B5793F]"} />
      </span>
      <span>
        <span className={`rp-display block text-2xl font-medium leading-none ${alert ? "text-red-600" : "text-[#262016]"}`}>{value}</span>
        <span className="block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] mt-1.5">{label}</span>
      </span>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/stats`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setStats)
      .catch(() => setStats(false));
  }, []);

  const sendDigest = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API}/api/admin/digest/run`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      if (d.status === "sent") toast.success(`Digest sent — ${d.items} update(s) to ${d.emails_sent} member(s)`);
      else toast.info(`Digest skipped: ${d.reason}`);
    } catch (e) {
      toast.error(e.message || "Digest failed");
    } finally {
      setSending(false);
    }
  };

  if (stats === null) return <p className="rp-display rp-italic text-[#5C5042]">One moment…</p>;
  if (stats === false) return <p className="text-sm text-[#8A7E6B]">Could not load stats.</p>;

  const c = stats.counts;
  const tasks = stats.upcoming_tasks || [];
  const overdueTasks = tasks.filter((t) => isOverdue(t.due_date));
  const upcomingTasks = tasks.filter((t) => !isOverdue(t.due_date));

  return (
    <div data-testid="admin-dashboard">
      {/* Main stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-8">
        <StatCard icon={Users} label="Members" value={stats.users_total} accent />
        <StatCard icon={TrendingUp} label="New this week" value={stats.users_7d} />
        <StatCard icon={Megaphone} label="Updates" value={c.updates} />
        <StatCard icon={Clapperboard} label="Reels" value={c.reels} />
        <StatCard icon={Heart} label="Reel likes" value={c.reel_likes} />
        <StatCard icon={Download} label="Downloads" value={c.downloads} />
        <StatCard icon={GraduationCap} label="Modules · Lessons" value={`${c.courses} · ${c.lessons}`} />
        <StatCard icon={MessageCircleQuestion} label="Tutor sessions" value={c.tutor_sessions} />
        <StatCard icon={Mail} label="Last digest" value={stats.last_digest ? `${stats.last_digest.sent} sent` : "—"} />
        <StatCard icon={Briefcase} label="Service requests" value={`${c.service_requests ?? 0} · ${c.service_new ?? 0} new`} accent={!!c.service_new} />
        <StatCard icon={FolderKanban} label="Active projects" value={c.projects_active ?? 0} />
        <StatCard icon={Lightbulb} label="Ideas captured" value={c.ideas_total ?? 0} />
      </div>

      {/* Task reminders section */}
      {(c.tasks_total > 0 || c.tasks_overdue > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="task-summary-stats">
          <div className="rp-foam bg-white/55 p-4 text-center">
            <LayoutGrid size={15} className="mx-auto mb-1.5 text-[#B5793F]" />
            <p className="text-xl font-bold text-[#262016]">{c.tasks_todo ?? 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#8A7E6B]">To Do</p>
          </div>
          <div className="rp-foam bg-white/55 p-4 text-center">
            <Clock size={15} className="mx-auto mb-1.5 text-[#B5793F]" />
            <p className="text-xl font-bold text-[#262016]">{c.tasks_in_progress ?? 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#8A7E6B]">In Progress</p>
          </div>
          <div className="rp-foam bg-white/55 p-4 text-center">
            <CheckSquare size={15} className="mx-auto mb-1.5 text-[#5C8A5C]" />
            <p className="text-xl font-bold text-[#262016]">{c.tasks_done ?? 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#8A7E6B]">Done</p>
          </div>
          {c.tasks_overdue > 0 ? (
            <div className="rp-foam p-4 text-center" style={{ background: "rgba(192,57,43,0.06)" }}>
              <AlertTriangle size={15} className="mx-auto mb-1.5 text-red-500" />
              <p className="text-xl font-bold text-red-600">{c.tasks_overdue}</p>
              <p className="text-[10px] uppercase tracking-wider text-red-500">Overdue</p>
            </div>
          ) : (
            <div className="rp-foam bg-white/55 p-4 text-center">
              <Flag size={15} className="mx-auto mb-1.5 text-[#C0392B]" />
              <p className="text-xl font-bold text-[#262016]">{c.tasks_high_priority ?? 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#8A7E6B]">High Priority</p>
            </div>
          )}
        </div>
      )}

      {/* Overdue + Upcoming tasks */}
      {tasks.length > 0 && (
        <div className="rp-foam bg-white/55 p-5 mb-6" data-testid="task-reminders">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={14} className={overdueTasks.length ? "text-red-500" : "text-[#B5793F]"} />
            <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium">
              {overdueTasks.length > 0 ? `${overdueTasks.length} Overdue · ` : ""}Upcoming Tasks
            </p>
          </div>
          <div className="space-y-2">
            {[...overdueTasks, ...upcomingTasks].map((t) => {
              const over = isOverdue(t.due_date);
              const subs = t.subtasks || [];
              const subsDone = subs.filter((s) => s.done).length;
              return (
                <div key={t.id}
                     className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg border ${over ? "border-red-200 bg-red-50/50" : "border-[#26201610] bg-white/50"}`}
                     data-testid={`task-reminder-${t.id}`}>
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: PRI_COLORS[t.priority] || PRI_COLORS.medium }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#262016] truncate">{t.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-[#8A7E6B] capitalize">{(t.board || "").replace("-", " ")}</span>
                      {subs.length > 0 && (
                        <span className="text-[10px] text-[#8A7E6B]">{subsDone}/{subs.length} done</span>
                      )}
                    </div>
                  </div>
                  <span className={`text-[11px] font-semibold shrink-0 flex items-center gap-1 ${over ? "text-red-600" : "text-[#8A7E6B]"}`}>
                    {over ? <AlertTriangle size={10} /> : <Calendar size={10} />}
                    {fmtDate(t.due_date)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-5">
        <div className="rp-foam bg-white/55 p-6" data-testid="recent-members">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium mb-4">Newest members</p>
          <div className="space-y-3">
            {stats.recent_users.map((u, i) => (
              <div key={i} className="flex items-center gap-3">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-[#262016] text-[#EDC9A3] flex items-center justify-center text-xs font-bold">
                    {(u.name || u.email || "?")[0].toUpperCase()}
                  </span>
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-[#262016] truncate">{u.name || "—"}</span>
                  <span className="block text-xs text-[#8A7E6B] truncate">{u.email}</span>
                </span>
                <span className="text-[11px] text-[#8A7E6B] shrink-0">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rp-foam bg-white/55 p-6 h-fit" data-testid="digest-panel">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium mb-2">Daily digest</p>
          <p className="text-[13px] text-[#5C5042] leading-relaxed mb-4">
            Members automatically get <strong>one email + in-app notification at 7pm IST</strong> summarising
            updates published that day. Nothing is sent on quiet days.
          </p>
          {stats.last_digest && (
            <p className="text-xs text-[#8A7E6B] mb-4">
              Last run: {stats.last_digest.date_key} — {stats.last_digest.items?.length || 0} item(s), {stats.last_digest.sent} email(s)
            </p>
          )}
          <button onClick={sendDigest} disabled={sending} data-testid="digest-send-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" }}>
            <Send size={13} /> {sending ? "Sending…" : "Send today's digest now"}
          </button>
        </div>
      </div>
    </div>
  );
}
